import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import RecordingStudio from "@/pages/RecordingStudio";
import JinglePreview from "@/pages/JinglePreview";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Checkout from "@/pages/Checkout";
import BatchSuccess from "@/pages/BatchSuccess";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

let lenisInstance = null;

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1.05 });
    lenisInstance = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);
}

// React Router keeps the previous page's scroll position on navigation (and
// Lenis preserves it too) — land every new page at the top instead.
// Also fires the first-party pageview beacon that feeds the admin funnel.
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (lenisInstance) lenisInstance.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    // Promo links (?code=SPRING50 on any page) carry the code through to checkout.
    const code = new URLSearchParams(search).get("code");
    if (code) localStorage.setItem("lbj_promo", code.trim().toUpperCase());
  }, [search]);
  useEffect(() => {
    // Never count admin-area traffic, and never count the owner's own browsing
    // while signed in to the admin — that is test traffic, not a prospect.
    if (pathname.startsWith("/admin") || localStorage.getItem("lbj_admin_token")) return;
    let vid = localStorage.getItem("lbj_vid");
    if (!vid) {
      vid = crypto.randomUUID();
      localStorage.setItem("lbj_vid", vid);
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: pathname, vid, ref: document.referrer || null }),
      keepalive: true,
    }).catch(() => {});
  }, [pathname]);
  return null;
}

function App() {
  useLenis();
  return (
    <div className="App grain">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/recording-studio/:jobId" element={<RecordingStudio />} />
          <Route path="/jingle-preview/:jobId" element={<JinglePreview />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/payment/batch-success" element={<BatchSuccess />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
